import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, Plus, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "../../context/SettingsContext";
import { SITE_ASSETS } from "../../mock";
import { clearToken, listSubmissions, deleteSubmission } from "../../lib/api";

const FONT_OPTIONS = [
  { value: "cormorant", label: "Cormorant Garamond + Inter (Default)" },
  { value: "playfair",  label: "Playfair Display + Inter" },
  { value: "cinzel",    label: "Cinzel + Poppins" },
  { value: "marcellus", label: "Marcellus + Poppins" },
  { value: "tenor",     label: "Tenor Sans + Poppins" },
];

const TABS = ["General", "Projects", "Services", "Amenities", "Testimonials", "FAQs", "Story", "Submissions"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const ctx = useSettings();
  const [tab, setTab] = useState("General");

  useEffect(() => {
    if (!localStorage.getItem("tamra_admin_token")) nav("/admin");
  }, [nav]);

  const logout = () => { clearToken(); nav("/admin"); };

  return (
    <div className="min-h-screen bg-[#F5EFE6] flex">
      <aside className="w-72 bg-[#1F1B17] text-[#E8DCC4] min-h-screen p-6 sticky top-0">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden">
            <img src={SITE_ASSETS.logo} alt="Tamra" className="w-9 h-9 object-contain" />
          </div>
          <div>
            <div className="font-display text-lg tracking-[0.28em] text-[#F5EFE6]">TAMRA</div>
            <div className="text-[10px] tracking-[0.42em] text-[#B8893C] mt-0.5">ADMIN</div>
          </div>
        </div>
        <nav className="space-y-1">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`w-full text-left px-4 py-3 text-[12px] tracking-[0.32em] uppercase transition ${tab === t ? "bg-[#5C2018] text-[#F5EFE6]" : "hover:bg-[#3A2F26]"}`}>{t}</button>
          ))}
        </nav>
        <div className="mt-10 space-y-2">
          <button onClick={logout} className="flex items-center gap-2 text-xs text-[#B8893C] hover:text-[#F5EFE6] transition">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="section-eyebrow mb-2">Dashboard</div>
            <h1 className="font-display text-3xl tracking-[0.05em] text-[#1F1B17]">{tab}</h1>
          </div>
          <a href="/" target="_blank" rel="noreferrer" className="btn-ghost">View Site</a>
        </div>

        {tab === "General"      && <GeneralTab />}
        {tab === "Projects"     && <ListTab keyName="projects"     fields={projectFields} />}
        {tab === "Services"     && <ListTab keyName="services"     fields={serviceFields} />}
        {tab === "Amenities"    && <ListTab keyName="amenities"    fields={amenityFields} />}
        {tab === "Testimonials" && <ListTab keyName="testimonials" fields={testimonialFields} />}
        {tab === "FAQs"         && <ListTab keyName="faqs"         fields={faqFields} />}
        {tab === "Story"        && <StoryTab />}
        {tab === "Submissions"  && <SubmissionsTab />}
      </main>
    </div>
  );
}

/* ---------------- General ---------------- */
function GeneralTab() {
  const { settings, updateSettings } = useSettings();
  const [s, setS] = useState(settings);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setContact = (k, v) => setS((x) => ({ ...x, contact: { ...x.contact, [k]: v } }));
  const setSocial  = (k, v) => setS((x) => ({ ...x, social:  { ...x.social,  [k]: v } }));

  const save = async () => { await updateSettings(s); toast.success("Settings saved."); };

  return (
    <div className="bg-white p-8 border border-[#EBE0CE] space-y-8 max-w-4xl">
      <Section title="Branding & Hero">
        <Grid>
          <Inp label="Brand Name"     value={s.brandName}     onChange={(v) => set("brandName", v)} />
          <Inp label="Brand Subtitle" value={s.brandSubtitle} onChange={(v) => set("brandSubtitle", v)} />
          <Inp label="Tagline"        value={s.tagline}       onChange={(v) => set("tagline", v)} />
          <Inp label="Hero Headline"  value={s.heroHeadline}  onChange={(v) => set("heroHeadline", v)} />
          <Inp label="Hero Subline"   value={s.heroSubline}   onChange={(v) => set("heroSubline", v)} full />
        </Grid>
      </Section>

      <Section title="Typography">
        <label className="block">
          <span className="text-[10px] tracking-[0.4em] uppercase text-[#B8893C]">Font Pair</span>
          <select value={s.fontPair} onChange={(e) => set("fontPair", e.target.value)}
            className="mt-2 w-full border-b border-[#EBE0CE] focus:border-[#B8893C] outline-none py-3 bg-transparent text-[#1F1B17]">
            {FONT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </Section>

      <Section title="Contact">
        <Grid>
          <Inp label="Phone"   value={s.contact.phone}   onChange={(v) => setContact("phone", v)} />
          <Inp label="Email"   value={s.contact.email}   onChange={(v) => setContact("email", v)} />
          <Inp label="Address" value={s.contact.address} onChange={(v) => setContact("address", v)} full />
          <Inp label="Hours"   value={s.contact.hours}   onChange={(v) => setContact("hours", v)} full />
        </Grid>
      </Section>

      <Section title="Social Links">
        <Grid>
          <Inp label="Instagram" value={s.social.instagram} onChange={(v) => setSocial("instagram", v)} />
          <Inp label="Facebook"  value={s.social.facebook}  onChange={(v) => setSocial("facebook", v)} />
          <Inp label="YouTube"   value={s.social.youtube}   onChange={(v) => setSocial("youtube", v)} />
          <Inp label="LinkedIn"  value={s.social.linkedin}  onChange={(v) => setSocial("linkedin", v)} />
        </Grid>
      </Section>

      <button onClick={save} className="btn-primary"><Save size={14} /> Save Changes</button>
    </div>
  );
}

/* ---------------- Story ---------------- */
function StoryTab() {
  const { story, updateStory } = useSettings();
  const [s, setS] = useState(story);
  useEffect(() => { setS(story); }, [story]);
  return (
    <div className="bg-white p-8 border border-[#EBE0CE] max-w-4xl space-y-6">
      <Inp label="Heading" value={s.heading} onChange={(v) => setS({ ...s, heading: v })} full />
      <label className="block">
        <span className="text-[10px] tracking-[0.4em] uppercase text-[#B8893C]">Body</span>
        <textarea value={s.body} onChange={(e) => setS({ ...s, body: e.target.value })} rows={8}
          className="mt-2 w-full border border-[#EBE0CE] focus:border-[#B8893C] outline-none p-3 bg-transparent text-[#1F1B17]" />
      </label>
      <button onClick={async () => { await updateStory(s); toast.success("Story saved."); }} className="btn-primary">
        <Save size={14} /> Save
      </button>
    </div>
  );
}

/* ---------------- Submissions ---------------- */
function SubmissionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await listSubmissions()); }
    catch (e) { toast.error("Failed to load submissions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await deleteSubmission(id);
      setItems((arr) => arr.filter((s) => s.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  if (loading) return <div className="bg-white p-8 border border-[#EBE0CE] text-[#3a322a]">Loading submissions…</div>;
  if (!items.length) return <div className="bg-white p-8 border border-[#EBE0CE] text-[#3a322a]">No submissions yet. Form entries will appear here.</div>;

  return (
    <div className="space-y-4">
      {items.map((s) => (
        <div key={s.id} className="bg-white p-6 border border-[#EBE0CE] flex gap-4">
          <div className="w-12 h-12 shrink-0 rounded-full bg-[#EBE0CE] flex items-center justify-center text-[#5C2018]"><Mail size={18} /></div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="font-display text-xl text-[#1F1B17]">{s.name}</div>
              <div className="text-xs text-[#9C7A3F]">{new Date(s.createdAt).toLocaleString()}</div>
            </div>
            <div className="text-xs text-[#9C7A3F] mt-1">{s.email} · {s.phone || "—"} · {s.project || "General enquiry"}</div>
            <p className="text-sm text-[#3a322a] mt-3 leading-relaxed">{s.message}</p>
          </div>
          <button onClick={() => remove(s.id)} className="text-[#9C7A3F] hover:text-[#5C2018]"><Trash2 size={16} /></button>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Generic List Tab ---------------- */
function ListTab({ keyName, fields }) {
  const ctx = useSettings();
  const list = ctx[keyName] || [];
  const [editing, setEditing] = useState(null); // item or null

  const blank = () => Object.fromEntries(fields.map((f) => [f.key, f.type === "list" ? [] : ""]));

  const startNew = () => setEditing({ id: `${keyName}_${Date.now()}`, ...blank() });

  const save = async () => {
    await ctx.upsertItem(keyName, editing);
    setEditing(null);
    toast.success("Saved.");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-3">
        <button onClick={startNew} className="btn-primary"><Plus size={14} /> Add New</button>
        <div className="space-y-2 mt-4">
          {list.map((it) => (
            <div key={it.id} className="bg-white border border-[#EBE0CE] p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-display text-lg text-[#1F1B17] truncate">{it.name || it.title || it.q || "Untitled"}</div>
                <div className="text-xs text-[#9C7A3F] truncate">{it.location || it.role || it.description || it.a || ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(it)} className="text-xs text-[#5C2018] underline">Edit</button>
                <button onClick={() => ctx.removeFromList(keyName, it.id)} className="text-[#9C7A3F] hover:text-[#5C2018]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white p-6 border border-[#EBE0CE]">
        {!editing ? (
          <div className="text-[#3a322a]">Select an item to edit, or click <em>Add New</em>.</div>
        ) : (
          <div className="space-y-5">
            {fields.map((f) => (
              <FieldRenderer key={f.key} field={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
            ))}
            <div className="flex gap-3">
              <button onClick={save} className="btn-primary"><Save size={14} /> Save</button>
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <label className="block">
        <span className="text-[10px] tracking-[0.4em] uppercase text-[#B8893C]">{field.label}</span>
        <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-[#EBE0CE] focus:border-[#B8893C] outline-none p-3 bg-transparent text-[#1F1B17]" />
      </label>
    );
  }
  if (field.type === "list") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <label className="block">
        <span className="text-[10px] tracking-[0.4em] uppercase text-[#B8893C]">{field.label} (comma-separated)</span>
        <input value={arr.join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
          className="mt-2 w-full border-b border-[#EBE0CE] focus:border-[#B8893C] outline-none py-3 bg-transparent text-[#1F1B17]" />
      </label>
    );
  }
  return <Inp label={field.label} value={value} onChange={onChange} full />;
}

/* ---------------- Field Configs ---------------- */
const projectFields = [
  { key: "name", label: "Name" }, { key: "location", label: "Location" }, { key: "type", label: "Type" },
  { key: "status", label: "Status" }, { key: "cover", label: "Cover Image URL" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "highlights", label: "Highlights", type: "list" },
];
const serviceFields = [
  { key: "title", label: "Title" }, { key: "icon", label: "Lucide Icon Name (e.g., Building2)" },
  { key: "description", label: "Description", type: "textarea" },
];
const amenityFields = [
  { key: "name", label: "Name" }, { key: "image", label: "Image URL" },
];
const testimonialFields = [
  { key: "name", label: "Name" }, { key: "role", label: "Role / Project" },
  { key: "quote", label: "Quote", type: "textarea" },
];
const faqFields = [
  { key: "q", label: "Question" }, { key: "a", label: "Answer", type: "textarea" },
];

/* ---------------- Tiny UI helpers ---------------- */
function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.5em] uppercase text-[#B8893C] mb-4">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-5">{children}</div>; }
function Inp({ label, value, onChange, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] tracking-[0.4em] uppercase text-[#B8893C]">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border-b border-[#EBE0CE] focus:border-[#B8893C] outline-none py-3 bg-transparent text-[#1F1B17]" />
    </label>
  );
}
